import { inject, Injectable, signal } from '@angular/core';
import { catchError, map, Observable, tap, throwError } from 'rxjs';

import { resolveCatalogErrorMessage } from '../catalog-error';
import { VariantsApiService } from '../api/variants-api.service';
import type { CreateVariantRequest, ProductVariantDto, UpdateVariantRequest } from '../models/variant.model';

@Injectable({ providedIn: 'root' })
export class VariantsStore {
  private readonly api = inject(VariantsApiService);

  readonly variants = signal<ProductVariantDto[]>([]);
  readonly loading = signal(false);
  readonly error = signal<string | null>(null);

  reset(): void {
    this.variants.set([]);
    this.loading.set(false);
    this.error.set(null);
  }

  load(productId: string): void {
    this.variants.set([]);
    this.loading.set(true);
    this.error.set(null);

    this.api.list(productId).subscribe({
      next: (response) => {
        this.variants.set(response.data);
        this.loading.set(false);
      },
      error: (error: unknown) => {
        this.error.set(resolveErrorMessage(error));
        this.loading.set(false);
      }
    });
  }

  create(productId: string, body: CreateVariantRequest): Observable<ProductVariantDto> {
    this.error.set(null);

    return this.api.create(productId, body).pipe(
      map((response) => response.data),
      tap((variant) => {
        this.variants.update((current) => [variant, ...current]);
      }),
      catchError((error: unknown) => {
        this.error.set(resolveErrorMessage(error));
        return throwError(() => error);
      })
    );
  }

  update(productId: string, id: string, body: UpdateVariantRequest): Observable<ProductVariantDto> {
    this.error.set(null);

    return this.api.update(productId, id, body).pipe(
      map((response) => response.data),
      tap((variant) => this.upsert(variant)),
      catchError((error: unknown) => {
        this.error.set(resolveErrorMessage(error));
        return throwError(() => error);
      })
    );
  }

  delete(productId: string, id: string): Observable<{ deleted: true }> {
    this.error.set(null);

    return this.api.delete(productId, id).pipe(
      map((response) => response.data),
      tap(() => {
        this.variants.update((current) => current.filter((variant) => variant.id !== id));
      }),
      catchError((error: unknown) => {
        this.error.set(resolveErrorMessage(error));
        return throwError(() => error);
      })
    );
  }

  private upsert(variant: ProductVariantDto): void {
    this.variants.update((current) => {
      const index = current.findIndex((item) => item.id === variant.id);

      if (index === -1) {
        return [variant, ...current];
      }

      return current.map((item) => (item.id === variant.id ? variant : item));
    });
  }
}

const resolveErrorMessage = (error: unknown): string => {
  return resolveCatalogErrorMessage(error, 'No pudimos completar la operación sobre variantes.');
};
